import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { pmApi } from '../../lib/pmApi';
import { Phone, Mail, Globe, ShieldAlert, Plus, Building2, UserCheck, UserPlus, Check, ExternalLink } from 'lucide-react';

const ContactCard = ({ icon: Icon, label, value, href, testId, danger }) => {
  if (!value) return null;
  const content = (
    <>
      <div className={`w-9 h-9 rounded ${danger ? 'bg-[#7F1D1D]/20' : 'bg-[#D4AF37]/10'} flex items-center justify-center shrink-0`}>
        <Icon className={`w-4 h-4 ${danger ? 'text-[#EF4444]' : 'text-[#D4AF37]'}`} />
      </div>
      <div className="min-w-0">
        <p className="text-[10px] uppercase tracking-[0.18em] text-[#525252] mb-0.5">{label}</p>
        <p className={`text-sm font-medium truncate ${danger ? 'text-[#FCA5A5]' : 'text-white'}`}>{value}</p>
      </div>
    </>
  );
  return href ? (
    <a
      href={href}
      data-testid={testId}
      className={`flex items-center gap-3 bg-[#141414] border ${danger ? 'border-[#7F1D1D]/60 hover:border-[#EF4444]' : 'border-[#262626] hover:border-[#D4AF37]'} rounded-md p-4 transition-colors`}
    >
      {content}
    </a>
  ) : (
    <div data-testid={testId} className="flex items-center gap-3 bg-[#141414] border border-[#262626] rounded-md p-4">
      {content}
    </div>
  );
};

const PmContacts = () => {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [pending, setPending] = useState([]);
  const [busyId, setBusyId] = useState('');

  const loadPending = () => pmApi.pendingContacts('open').then((d) => setPending(d.items || [])).catch(() => setPending([]));

  useEffect(() => {
    pmApi.contacts().then(setData).catch((e) => setErr(e.response?.data?.detail || e.message));
    loadPending();
  }, []);

  const resolve = async (id, action) => {
    setBusyId(id);
    try {
      if (action === 'confirm') { await pmApi.confirmPendingContact(id); toast.success('Contact confirmed & added'); }
      else { await pmApi.keepPendingExternal(id); toast.success('Kept as external contact'); }
      await loadPending();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not update contact');
    } finally { setBusyId(''); }
  };

  if (err) return <div data-testid="pm-contacts-error" className="text-[#EF4444]">{err}</div>;
  if (!data) return <div className="text-[#A3A3A3]">Loading…</div>;

  const nx = data.nexflow || {};

  return (
    <div data-testid="pm-contacts-page" className="space-y-8 max-w-5xl">
      <div>
        <p className="text-[#D4AF37] text-xs font-bold tracking-[0.3em] mb-1">CONTACTS</p>
        <h1 className="font-['Outfit'] text-3xl font-bold text-white">NexFlow Contact Directory</h1>
        {nx.intro_blurb && <p className="text-[#A3A3A3] text-sm mt-2 max-w-2xl">{nx.intro_blurb}</p>}
      </div>

      {/* Contact Review Needed — unlisted requesters NexFlow captured on a job */}
      {pending.length > 0 && (
        <section data-testid="pm-contact-review-section">
          <p className="text-amber-300 text-[10px] font-bold tracking-[0.2em] uppercase mb-3 flex items-center gap-1.5">
            <UserPlus className="w-3.5 h-3.5" /> Employee / Contact Review Needed ({pending.length})
          </p>
          <div className="space-y-2">
            {pending.map((c) => (
              <div key={c.id} data-testid={`pm-pending-contact-${c.id}`} className="bg-[#141414] border border-amber-700/40 rounded-md p-4 flex flex-wrap items-center gap-3">
                <div className="min-w-0 flex-1">
                  <p className="text-white text-sm font-medium">{c.name} <span className="text-amber-400 text-[10px] font-bold uppercase ml-1">Unverified</span></p>
                  <p className="text-[#A3A3A3] text-xs mt-0.5">
                    {[c.phone, c.email].filter(Boolean).join(' · ') || 'No contact details provided'}
                    {c.created_by_name && <span className="text-[#525252]"> — scheduled by {c.created_by_name}</span>}
                  </p>
                  {c.notes && <p className="text-[#525252] text-xs mt-0.5">{c.notes}</p>}
                </div>
                <div className="flex gap-2">
                  <button data-testid={`pm-pending-confirm-${c.id}`} disabled={busyId === c.id} onClick={() => resolve(c.id, 'confirm')}
                    className="inline-flex items-center gap-1 px-3 py-2 rounded-md bg-green-600 hover:bg-green-500 text-white text-xs font-bold disabled:opacity-50">
                    <Check className="w-3.5 h-3.5" /> Confirm / Add
                  </button>
                  <button data-testid={`pm-pending-external-${c.id}`} disabled={busyId === c.id} onClick={() => resolve(c.id, 'external')}
                    className="inline-flex items-center gap-1 px-3 py-2 rounded-md bg-[#0A0A0A] border border-[#262626] text-[#A3A3A3] hover:text-white text-xs font-bold disabled:opacity-50">
                    <ExternalLink className="w-3.5 h-3.5" /> Keep External
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* NexFlow */}
      <section>
        <p className="text-[#A3A3A3] text-[10px] font-bold tracking-[0.2em] uppercase mb-3">Reach NexFlow</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <ContactCard testId="pm-contact-emergency" icon={ShieldAlert} label="Emergency Line — 24/7" value={nx.emergency_phone} href={nx.emergency_phone ? `tel:${nx.emergency_phone.replace(/\D/g,'')}` : null} danger />
          <ContactCard testId="pm-contact-general-phone" icon={Phone} label="General Phone" value={nx.general_phone} href={nx.general_phone ? `tel:${nx.general_phone.replace(/\D/g,'')}` : null} />
          <ContactCard testId="pm-contact-general-email" icon={Mail} label="General Email" value={nx.general_email} href={nx.general_email ? `mailto:${nx.general_email}` : null} />
          <ContactCard testId="pm-contact-billing" icon={Mail} label="Billing" value={nx.billing_email} href={nx.billing_email ? `mailto:${nx.billing_email}` : null} />
          <ContactCard testId="pm-contact-dispatch" icon={Mail} label="Dispatch" value={nx.dispatch_email} href={nx.dispatch_email ? `mailto:${nx.dispatch_email}` : null} />
          <ContactCard testId="pm-contact-website" icon={Globe} label="Website" value={nx.website} href={nx.website || null} />
        </div>
        {nx.support_hours && (
          <p data-testid="pm-contact-hours" className="text-[#525252] text-xs mt-3">Hours: {nx.support_hours}</p>
        )}
      </section>

      {/* Account Manager */}
      {(nx.account_manager_name || nx.account_manager_phone || nx.account_manager_email) && (
        <section>
          <p className="text-[#A3A3A3] text-[10px] font-bold tracking-[0.2em] uppercase mb-3">Your NexFlow Account Manager</p>
          <div className="bg-[#141414] border border-[#262626] rounded-md p-5 flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-[#D4AF37]/10 flex items-center justify-center">
              <UserCheck className="w-6 h-6 text-[#D4AF37]" />
            </div>
            <div className="flex-1 min-w-0">
              {nx.account_manager_name && <p className="text-white font-medium">{nx.account_manager_name}</p>}
              <div className="flex flex-wrap gap-4 mt-1">
                {nx.account_manager_phone && (
                  <a href={`tel:${nx.account_manager_phone.replace(/\D/g,'')}`} className="text-sm text-[#A3A3A3] hover:text-[#D4AF37]"><Phone className="w-3 h-3 inline mr-1" />{nx.account_manager_phone}</a>
                )}
                {nx.account_manager_email && (
                  <a href={`mailto:${nx.account_manager_email}`} className="text-sm text-[#A3A3A3] hover:text-[#D4AF37]"><Mail className="w-3 h-3 inline mr-1" />{nx.account_manager_email}</a>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Quick actions */}
      <section>
        <p className="text-[#A3A3A3] text-[10px] font-bold tracking-[0.2em] uppercase mb-3">Quick Actions</p>
        <div className="flex flex-wrap gap-2">
          <Link to="/pm/submit" data-testid="pm-contact-submit-request" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-[#D4AF37] text-[#0A0A0A] font-medium hover:bg-[#F3E5AB] text-sm">
            <Plus className="w-4 h-4" /> Submit Service Request
          </Link>
          <Link to="/pm/work-orders" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-[#141414] border border-[#262626] text-[#A3A3A3] hover:text-white text-sm">
            View Open Work Orders →
          </Link>
        </div>
      </section>

      {/* Company contacts */}
      {data.company && (data.company_contacts?.length > 0) && (
        <section>
          <p className="text-[#A3A3A3] text-[10px] font-bold tracking-[0.2em] uppercase mb-3">{data.company.name} Contacts</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {data.company_contacts.map((c, i) => (
              <div key={i} data-testid={`pm-company-contact-${i}`} className="bg-[#141414] border border-[#262626] rounded-md p-4">
                <p className="text-white text-sm font-medium">{c.name}</p>
                <p className="text-[#A3A3A3] text-xs capitalize mt-0.5">{(c.role || '').replace(/_/g, ' ')}</p>
                <div className="flex flex-wrap gap-3 mt-2 text-xs">
                  {c.phone && <a href={`tel:${(c.phone||'').replace(/\D/g,'')}`} className="text-[#D4AF37] hover:text-[#F3E5AB]"><Phone className="w-3 h-3 inline mr-1" />{c.phone}</a>}
                  {c.email && <a href={`mailto:${c.email}`} className="text-[#D4AF37] hover:text-[#F3E5AB]"><Mail className="w-3 h-3 inline mr-1" />{c.email}</a>}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Property contacts */}
      {data.property_contacts?.length > 0 && (
        <section>
          <p className="text-[#A3A3A3] text-[10px] font-bold tracking-[0.2em] uppercase mb-3">Property-Level Contacts</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {data.property_contacts.map((c, i) => (
              <div key={i} data-testid={`pm-property-contact-${i}`} className="bg-[#141414] border border-[#262626] rounded-md p-4">
                <div className="flex items-center gap-1.5 mb-1">
                  <Building2 className="w-3 h-3 text-[#525252]" />
                  <Link to={`/pm/properties/${c.property_id}`} className="text-[#525252] text-[10px] uppercase tracking-wider hover:text-[#D4AF37]">{c.property_name}</Link>
                </div>
                <p className="text-white text-sm font-medium">{c.name}</p>
                <p className="text-[#A3A3A3] text-xs capitalize mt-0.5">{(c.role || '').replace(/_/g, ' ')}</p>
                <div className="flex flex-wrap gap-3 mt-2 text-xs">
                  {c.phone && <a href={`tel:${(c.phone||'').replace(/\D/g,'')}`} className="text-[#D4AF37] hover:text-[#F3E5AB]"><Phone className="w-3 h-3 inline mr-1" />{c.phone}</a>}
                  {c.email && <a href={`mailto:${c.email}`} className="text-[#D4AF37] hover:text-[#F3E5AB]"><Mail className="w-3 h-3 inline mr-1" />{c.email}</a>}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

export default PmContacts;
